import { db, auth } from '@/js/firebase';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  getDocs,
  addDoc,
  // deleteDoc,
  serverTimestamp,
  // Timestamp,
  onSnapshot,
  orderBy,
  limit
} from 'firebase/firestore';
import { logger } from './loggerService';
import { toastBridge } from './toastBridge';
import { companyCache } from './companyCache';

class CompanyService {
  constructor() {
    this.logger = logger;
    this.toast = toastBridge;
  }

  async getCompany(companyId) {
    try {
      const companyRef = doc(db, 'companies', companyId);
      const companySnap = await getDoc(companyRef);

      if (companySnap.exists()) {
        return { id: companySnap.id, ...companySnap.data() };
      }

      throw new Error(`Company ${companyId} not found`);
    } catch (error) {
      this.logger.error('[CompanyService] Failed to get company', {
        error: error.message,
        companyId
      });
      throw error;
    }
  }

  async updateCompany(companyId, updates, showToast = true) {
    try {
      const companyRef = doc(db, 'companies', companyId);

      const cleanedUpdates = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      await updateDoc(companyRef, cleanedUpdates);

      // Bust the cached copy so navbar/theme re-read fresh logo+branding.
      companyCache.removeCompany(companyId);

      this.logger.info('[CompanyService] Company updated successfully', {
        companyId
      });

      if (showToast) {
        this.toast.success('Company updated successfully');
      }

      return { success: true };
    } catch (error) {
      this.logger.error('[CompanyService] Failed to update company', {
        error: error.message,
        companyId
      });
      this.toast.error(`Failed to update company: ${error.message}`);
      throw error;
    }
  }

  /**
   * Log an activity entry for a company
   * @param {string} companyId - Company ID
   * @param {string} action - Action type (created, field_update, contract_created, etc.)
   * @param {string} field - Field name or description
   * @param {any} oldValue - Previous value
   * @param {any} newValue - New value
   * @param {string} note - Optional note
   */
  async logActivity(companyId, action, field = null, oldValue = null, newValue = null, note = null) {
    try {
      const user = auth.currentUser;
      if (!user) {
        this.logger.warn('[CompanyService] Cannot log activity - no authenticated user');
        return;
      }

      // Determine user type from custom claims
      let userType = 'system';
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        const claims = idTokenResult.claims;

        if (claims.admin || claims.super) {
          userType = 'admin';
        } else if (claims.user) {
          userType = 'client';
        }
      }

      const userName = user.displayName || user.email || 'Unknown User';

      // Create activity log entry
      const activityData = {
        action,
        field,
        oldValue: oldValue !== undefined ? oldValue : null,
        newValue: newValue !== undefined ? newValue : null,
        userId: user.uid,
        userName,
        userType,
        timestamp: serverTimestamp(),
        note
      };

      // Remove null values to keep documents clean
      Object.keys(activityData).forEach(key => {
        if (activityData[key] === null || activityData[key] === undefined) {
          delete activityData[key];
        }
      });

      const activityRef = collection(db, 'companies', companyId, 'activity');
      await addDoc(activityRef, activityData);

      this.logger.debug('[CompanyService] Activity logged', {
        companyId,
        action,
        field
      });
    } catch (error) {
      this.logger.error('[CompanyService] Failed to log activity', {
        error: error.message,
        companyId,
        action
      });
      // Don't throw - activity logging shouldn't break the main flow
    }
  }

  /**
   * Get activity logs for a company
   * @param {string} companyId - Company ID
   * @param {number} limitCount - Maximum number of logs to fetch (default 50)
   * @returns {Promise<Array>} Array of activity log entries
   */
  async getCompanyActivity(companyId, limitCount = 50) {
    try {
      const activityRef = collection(db, 'companies', companyId, 'activity');
      const q = query(activityRef, orderBy('timestamp', 'desc'), limit(limitCount));

      const querySnapshot = await getDocs(q);
      const activities = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        activities.push({
          id: doc.id,
          ...data,
          // Convert Firestore Timestamp to Date for easier handling
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : data.timestamp
        });
      });

      this.logger.debug('[CompanyService] Fetched company activity', {
        companyId,
        count: activities.length
      });

      return activities;
    } catch (error) {
      this.logger.error('[CompanyService] Failed to get company activity', {
        error: error.message,
        companyId
      });
      throw error;
    }
  }

  /**
   * Subscribe to real-time company activity updates
   * @param {string} companyId - Company ID
   * @param {Function} callback - Callback function to receive activity updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToCompanyActivity(companyId, callback) {
    try {
      const activityRef = collection(db, 'companies', companyId, 'activity');
      const q = query(activityRef, orderBy('timestamp', 'desc'), limit(50));

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const activities = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          activities.push({
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : data.timestamp
          });
        });

        callback(activities);
      }, (error) => {
        this.logger.error('[CompanyService] Error in activity subscription', {
          error: error.message,
          companyId
        });
      });

      this.logger.debug('[CompanyService] Subscribed to company activity', {
        companyId
      });

      return unsubscribe;
    } catch (error) {
      this.logger.error('[CompanyService] Failed to subscribe to company activity', {
        error: error.message,
        companyId
      });
      throw error;
    }
  }
}

export const companyService = new CompanyService();
